import { CircularProgress } from "@material-ui/core";
import Modal from "@material-ui/core/Modal";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontSize } from "config/font";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ButtonComponent } from "../general/button/button.component";
import { Checkbox } from "../general/checkbox/checkbox.component";
import { SearchComponent } from "../general/search/search.component";
import { TopModal } from "../general/topModal/topModal.component";
import { Logo } from "../salary/salary.styled";
import {
  ActionContainer,
  AssignWorkersSearch,
  Column,
  ListContainer,
  OrgStructureModalBody,
  SimpleRow,
} from "./organizationalStructure.styled";

type Props = {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  workgroupId?: number;
  onClick: (checked: any[]) => void;
  title: string;
  info: string;
  buttonTitle: string;
  selected?: Worker[];
  active?: boolean;
  filter: string;
  showPaymentMethod?: boolean;
};

export interface Worker {
  id: number;
  workerId: number;
  firstName: string;
  lastName: string;
  personalIdentityNumber: string;
  employmentInfo: {
    current: {
      employmentType: string;
      paymentType: string;
    };
  };
  selectedTeamId: number;
  selectedCustomerProduct?: string;
}

export const ChooseWorkersModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [workerList, setWorkerList] = useState<Worker[]>([]);
  const [companyState] = useCompanyStore();
  const [checked, setChecked] = useState<Worker[]>(
    props.selected && props.selected.length > 0 ? props.selected : [],
  );
  const [isLoading, setIsLoading] = useState(false);

  const filteredWorkers = useMemo(() => {
    if (!inputValue) return workerList;

    return workerList.filter((worker) =>
      `${worker.firstName} ${worker.lastName}`
        .toLowerCase()
        .includes(inputValue.toLowerCase()),
    );
  }, [workerList, inputValue]);

  const getCompanyWorkers = async () => {
    if (!companyState.company?.id) return;

    setIsLoading(true);
    const pageSize = 50;
    let allWorkers: Worker[] = [];
    let currentPage = 0;
    let hasMoreData = true;

    try {
      while (hasMoreData) {
        const res =
          await Api().company.organizationalStructure.fetchCompanyWorkers(
            companyState.company.id,
            currentPage,
            pageSize,
            props.active ?? false,
            props.filter,
          );

        allWorkers = [...allWorkers, ...res.data.workers];

        const totalItems = res.data.total;
        const itemsLoaded = (currentPage + 1) * pageSize;

        if (itemsLoaded >= totalItems) {
          hasMoreData = false;
        } else {
          currentPage++;
        }
      }

      setWorkerList(allWorkers);
    } catch (e) {
      console.error("Error when getting workers", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCompanyWorkers();
  }, [companyState.company?.id]);

  return (
    <>
      <Modal open={props.showModal} onClose={() => props.setShowModal(false)}>
        <OrgStructureModalBody>
          <TopModal
            title={props.title}
            setClose={() => props.setShowModal(false)}
          />
          <Text>{props.info}</Text>
          <AssignWorkersSearch>
            <SearchComponent
              placeholder={t("OrganizationalStructure.SearchWorkerPlaceholder")}
              value={inputValue}
              onChange={(e: any) => {
                setInputValue(e.currentTarget.value);
              }}
            />
          </AssignWorkersSearch>
          {!isLoading ? (
            <ListContainer>
              {filteredWorkers.length > 0 &&
                filteredWorkers.map((worker) => (
                  <SimpleRow
                    key={worker.workerId}
                    style={{
                      justifyContent: "space-between",
                      borderBottom: `1px solid ${Color.SeaBlue200}`,
                      width: "95%",
                    }}
                  >
                    <Column>
                      <Text
                        style={{ marginBottom: 0 }}
                      >{`${worker.firstName} ${worker.lastName}`}</Text>
                      <Text fontSize={FontSize.Tiny} style={{ marginTop: 0 }}>
                        {worker.personalIdentityNumber}
                      </Text>
                    </Column>
                    <SimpleRow>
                      <Text
                        fontSize={FontSize.Tiny}
                        style={{
                          padding: 10,
                          backgroundColor: Color.LightGrey03,
                          borderRadius: 5,
                          marginRight: 35,
                          height: 35,
                        }}
                      >
                        {t(
                          `OrganizationalStructure.${worker.employmentInfo.current.employmentType}`,
                        )}
                      </Text>
                      {worker.employmentInfo.current.paymentType ===
                        PaymentMethod.SIMFLEXITY_PAY &&
                        props.showPaymentMethod && (
                          <Logo
                            src={SimflexityPayLogo}
                            style={{
                              height: 14,
                              width: "auto",
                              marginLeft: -20,
                              marginRight: 30,
                              marginTop: 18,
                            }}
                            alt="SimflexityPay logo"
                          />
                        )}
                      <Checkbox
                        name={worker.workerId.toString()}
                        checked={checked.some(
                          (w) => w.workerId === worker.workerId,
                        )}
                        onChange={(isChecked) =>
                          setChecked((prev) =>
                            isChecked
                              ? [...prev, worker]
                              : prev.filter((w) => w !== worker),
                          )
                        }
                      />
                    </SimpleRow>
                  </SimpleRow>
                ))}
            </ListContainer>
          ) : (
            <CircularProgress
              style={{ width: "5%", height: "5%", margin: "0 auto" }}
            />
          )}
          <ActionContainer style={{ margin: "0 auto" }}>
            <ButtonComponent
              size={"small"}
              onClick={() => props.onClick(checked)}
              action
              marginTop={10}
              marginBottom={20}
              disabled={checked.length === 0}
            >
              {props.buttonTitle}
            </ButtonComponent>
          </ActionContainer>
        </OrgStructureModalBody>
      </Modal>
    </>
  );
};
